// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
// import { getFulfillmentLabel, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors"; // Store Custom Code
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import MapPin from "@insite/mobius/Icons/MapPin";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import constants from "@store/constants";
import PinLine from "@store/Library/Components/Toolkit/Icons/Pin/PinLine";
import { headerIconStyles } from "@store/Themes/common/mixins/header";
import { getFulfillmentLabel } from "@store/Framework/Store/Context/ContextSelectors";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentLabel: getFulfillmentLabel(state),
    isDrawerOpen: state.components.addressDrawer.isOpen,
    isPunchOutSession: getIsPunchOutSession(state),
});

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    // Store Custom Code
    link?: LinkPresentationProps;
    largeRowTitle?: TypographyPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            /* Store Custom Code - Start */
            margin-left: ${pxToRem(10)};
            width: calc(100% - ${pxToRem(10)});
            padding: ${pxToRem(6)} 0;
            /* Store Custom Code - End */
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: ${pxToRem(10)}; /* Store Custom Code */
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
    /* Store Custom Code - Start */
    link: {
        css: css`
            ${headerIconStyles()}
            width: calc(100% - ${pxToRem(10)});
            padding: ${pxToRem(6)} 0;
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
        icon: {
            iconProps: {
                src: PinLine,
                css: css`
                    ${x =>
                        breakpointValues(
                            x.theme.design.header.iconButton.size.map(x => (x ? iconScalableStyles(x) : null)),
                        )}
                `,
            },
        },
        typographyProps: {
            css: css`
                margin-left: ${pxToRem(10)};
                max-width: ${pxToRem(150)};
                color: ${getColor("text.link")};
                font-size: ${x => x.theme.design.typography.size.smaller};
            `,
            ellipsis: false,
        },
    },
    largeRowTitle: {
        ellipsis: true,
        css: css`
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    /* Store Custom Code - End */
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentLabel,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
}) => {
    const link = usePageLinkByPageType(constants.pageType.locationFinder); /* Store Custom Code */

    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    return (
        /* Store Custom Code - Start */
        <Hidden below="md">
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <Link {...styles.link} href={link?.url}>
                    <Typography {...styles.largeRowTitle}>{translate("You're shopping")}</Typography>
                    {fulfillmentLabel}
                </Link>
            </StyledWrapper>
        </Hidden>
        /* Store Custom Code - End */
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
